import register from 'preact-custom-element'
import { fetchProductByCollectionHandle } from 'libs/shopify-product'
import { useState, useEffect, useMemo, useCallback } from 'preact/hooks'
import { normalizeProducts } from '@/components/libs/shopify-normalize'
import PlpCard from 'snippets/plp-card/plp-card'
import { getAvailable, getVariants, getOptions, getTags, getPrice, checkItemInCart, getImagesCard, getProductSize } from 'libs/searchspring/searchspring-utils'
// NostoProductCard import removed - not used in this component
import { SkeletonProduct } from 'snippets/skeleton/skeleton'
import LoaderSpin from 'snippets/loader-spin/loader-spin'
import { fetchProductsLinked } from 'helpers/fetch'
import { addCartItem } from 'libs/cart'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import './searchspring-product-bundle.css'

// Custom checkbox component
function ProductCheckbox({ productId, isChecked, onToggle }) {
  return (
    <div
      className={`absolute top-4 right-4 md:top-2 md:right-2 z-20 w-6 h-6 border-2 rounded cursor-pointer transition-all duration-200 ${
        isChecked
          ? 'bg-primary border-primary'
          : 'bg-white border-grey-300 hover:border-grey-400'
      }`}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onToggle(productId)
      }}
    >
      {isChecked && (
        <svg
          className="w-4 h-4 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </div>
  )
}

function SearchspringProductBundle(props) {
  const settings = window[`popularProductConfig${props.index}`]
  const [products, setProducts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [checkedProducts, setCheckedProducts] = useState(new Set())
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [selectedVariantByProductId, setSelectedVariantByProductId] = useState({})

  // Calculate subtotal for selected products
  const subtotal = useMemo(() => {
    let total = 0
    let count = 0
    products.forEach(product => {
      if (checkedProducts.has(product.id)) {
        const override = selectedVariantByProductId[product.id]
        const priceDollars = override?.price != null
          ? (Number(override.price) / 100)
          : (parseFloat(product.price) || 0)
        total += priceDollars
        count++
      }
    })
    return { total, count }
  }, [products, checkedProducts, selectedVariantByProductId])

  // Add selected items to cart
  const addSelectedToCart = useCallback(async () => {
    if (subtotal.count === 0) return

    setIsAddingToCart(true)
    const selectedProducts = products.filter(product => checkedProducts.has(product.id))
    try {
      // Add each selected product to cart using the same pattern as other add to cart buttons
      for (const product of selectedProducts) {
        const override = selectedVariantByProductId[product.id]
        const variantId = override?.variantId || product.variants?.[0]?.id || product.id
        const cartSingle = await addCartItem(variantId, {}, 1)
        if (cartSingle) {
          globalEmitter.emit(emitterProps.cart.add, cartSingle)
        }
      }
    } catch (error) {
      console.error('Error adding items to cart:', error)
    } finally {
      setIsAddingToCart(false)
    }
  }, [products, checkedProducts, subtotal.count, selectedVariantByProductId])
  const handleCheckboxToggle = useCallback((productId) => {
    setCheckedProducts(prev => {
      const newSet = new Set(prev)
      if (newSet.has(productId)) {
        newSet.delete(productId)
      } else {
        newSet.add(productId)
      }
      return newSet
    })
  }, [])

  const getSearchspringRecommendedProducts = async () => {
    try {
      if (settings.searchspringEnabled && settings.searchspringSiteId) {
        let productIds = []
        if (settings.searchResults) {
          productIds = settings.searchResults.map(item => item.id)
        }
        if (settings.productId) {
          productIds.push(settings.productId)
        }
        const options = {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            profiles: [{ tag: settings.searchspringTag }],
            categories: [settings.productsCollection],
            products: productIds
          })
        }

        const response = await fetch(
          `https://${settings.searchspringSiteId}.a.searchspring.io/boost/${settings.searchspringSiteId}/recommend`,
          options
        )

        const data = await response.json()
        const dataObject = data[0]

        if (dataObject?.results?.length > 0) {
          const result = normalizeProducts(dataObject.results, 'searchspring-recommended')
          return result
        }
      }

      // fallback to Shopify GraphQL related products
      const recommendations = await getRelatedProducts(settings.productsCollection)
      if (recommendations && recommendations.length > 0) {
        return normalizeProducts(recommendations, 'shopify-graphql')
      }

      return []
    } catch (err) {
      console.error('Searchspring fetch error:', err)
      return []
    }
  }

  const getRelatedProducts = async (collection) => {
    if (collection === null) return null
    const filters = [
      {
        available: true
      }
    ]
    return await fetchProductByCollectionHandle(collection, filters, 3) // Limit to 3 products
  }

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsLoading(true)
        const response = await getSearchspringRecommendedProducts()
        if (response?.length) {
          await fetchProductsLinked(response)
          setProducts(response)
          setCheckedProducts(new Set(response.slice(0, 3).map(p => p.id)))
        } else {
          setProducts([])
        }
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }
    loadProducts()
  }, [])

  // Memoized product cards to prevent re-renders
  const memoizedProductCards = useMemo(() => {
    return products.slice(0, 3).map((product, index) => {
      const isChecked = checkedProducts.has(product.id)
      const onCurrentProductChange = ({ productId, variantId, price }) => {
        const formattedPrice = price != null ? (Number(price) / 100).toFixed(2) : null
        if (!productId && !variantId) return
        setSelectedVariantByProductId((prev) => ({
          ...prev,
          [product.id]: { productId, variantId, price }
        }))
      }
      const colSpanClass = index === 0 ? 'col-span-2 lg:col-span-1' : ''
      const mobilePseudoBefore = index === 0 ? "after:content-[''] after:bg-plus after:w-5 after:h-5 after:bg-center after:text-2xl after:text-grey-500 after:font-bold after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-[-12px] lg:after:content-none" : ''
      return (
        <div key={product.id} className={`relative flex flex-col lg:flex-row lg:items-center lg:flex-1 ${colSpanClass} ${mobilePseudoBefore} lg:[&:not(:first-child)::before]:content-[''] lg:[&:not(:first-child)::before]:bg-plus lg:[&:not(:first-child)::before]:w-5 lg:[&:not(:first-child)::before]:h-5 lg:[&:not(:first-child)::before]:bg-center lg:[&:not(:first-child)::before]:text-2xl lg:[&:not(:first-child)::before]:text-grey-500 lg:[&:not(:first-child)::before]:font-bold lg:[&:not(:first-child)::before]:absolute xl:[&:not(:first-child)::before]:left-[-24px] lg:[&:not(:first-child)::before]:left-[-24px]`}>
          <div className="relative w-full">
            <ProductCheckbox
              productId={product.id}
              isChecked={isChecked}
              onToggle={handleCheckboxToggle}
            />
            <PlpCard
              id={product.id}
              title={product.title}
              subtitle={product.sizes}
              isInCart={checkItemInCart(product.uid)}
              handle={product.handle}
              imageUrl={[product.media[0]]}
              price={getPrice(product.price)}
              originalPrice={getPrice(product.compareAtPriceMin)}
              isShowBadge={true}
              isSSBundle={true}
              tags={getTags(product.tags)}
              showSwatches={true}
              linkedColors={product.linkedColors}
              currentSize={product.sizes}
              sku={product.sku}
              ratioImage="aspect-[1/1] overflow-hidden"
              className="plp-card-grid mb-5 lg:mb-4"
              onCurrentProductChange={onCurrentProductChange}
            />
          </div>
        </div>
      )
    })
  }, [products, checkedProducts, handleCheckboxToggle])

  return (
    <div className="container relative overflow-hidden">
      <div className="carousel-heading flex flex-col gap-1 text-left md:gap-2 mb-4">
        {props.heading && <div className="title text-3xl !font-normal lg-max:mb-1 font-heading">
          {props.heading}
        </div>}
        {props.sub_heading && <div className="subtitle text-sm" dangerouslySetInnerHTML={{ __html: props.sub_heading }}></div>}
      </div>
      <div className="w-full">
        {isLoading ? (
          <div className="relative">
            <div className="hidden grid-cols-1 gap-4 2xl:grid 2xl:grid-cols-5">{SkeletonProduct(5)}</div>
            <div className="grid grid-cols-1 gap-4 lg-max:hidden lg:grid-cols-4 2xl:hidden">{SkeletonProduct(4)}</div>
            <div className="h-72 lg:hidden"><LoaderSpin width="8" height="8" /></div>
          </div>
        ) : (
          (products && products.length > 0) ? (
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 lg:!gap-7 md:gap-7 gap-0 lg:items-start">
                {memoizedProductCards}
                <div className="col-span-2 lg:col-span-1 bg-info-bg h-full flex flex-col lg:gap-4 gap-8 xl:px-[26px] lg:px-2 justify-center">
                  <div className="flex flex-col items-center justify-center gap-[15px] lg:h-full lg:pt-0 pt-6 text-center">
                    {subtotal.count > 0 ? (
                      <>
                        <div className="h5 font-normal text-grey-1000 text-2xl">{subtotal.count === 1 ? settings.itemText.replace('$item_count', subtotal.count) : settings.itemsText.replace('$item_count', subtotal.count)}</div>
                        <div className="h6 text-xl text-grey-1000">
                          ${subtotal.total.toFixed(2)}
                        </div>
                        {props.badge && <div className="bg-primary text-[10px] leading-none inline font-semibold uppercase text-white px-2 py-1 rounded-[4px]">{props.badge}</div>}
                      </>
                    ) : (
                      'Select items to see subtotal'
                    )}
                  </div>
                  {/* Add to Cart button */}
                  {subtotal.count > 0 && (
                    <div className="px-2 pb-6 mt-auto w-full text-center">
                      <button
                        onClick={addSelectedToCart}
                        className="button-primary w-full mx-auto max-w-[268px] relative"
                        disabled={subtotal.count === 0 || isAddingToCart}
                      >
                        {isAddingToCart ? (
                          <>
                            <LoaderSpin width="5" height="5" spinClass="border-t-white" />
                            <span className="ml-2">Adding to Cart...</span>
                          </>
                        ) : (
                          subtotal.count === 3
                            ? translate.product.ssBundleAddAllToCart
                            : translate.product.ssBundleAddToCart.replace('$items_count', subtotal.count).replace('$item_type', subtotal.count === 1 ? 'Item' : 'Items')
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-lg font-medium">No products found</div>
          )
        )}
      </div>
    </div>
  )
}

if (!customElements.get('searchspring-product-bundle')) {
  register(SearchspringProductBundle, 'searchspring-product-bundle', ['heading', 'sub_heading', 'index', 'itemText', 'itemsText'])
}
